import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import "../styles/UpdateSKUDetails.css";
import axios from 'axios';
import { GridLoader } from 'react-spinners';

function UpdateSKUDetails() {
  const navigate = useNavigate();

  const [license,         setLicense]         = useState('');
  const [productName,     setProductName]      = useState('');
  const [productImage,    setProductImage]     = useState(null);
  const [newProductImage, setNewProductImage]  = useState(null);
  const [gtin,            setGtin]             = useState('');
  const [genericName,     setGenericName]      = useState('');
  const [skuId,           setSKUID]            = useState('');
  const [manufacturingLic,setManufacturingLic] = useState('');
  const [groupId,         setGroupId]          = useState('');
  const [visitUrl,        setVisitUrl]         = useState('');
  const [excipients,      setExcipients]       = useState('');
  const [fieldErrors,     setFieldErrors]      = useState({});
  const [groupIds,        setGroupIds]         = useState([]);
  const [skuImage,        setSkuImage]         = useState(null);
  const [changeImage,     setChangeImage]      = useState(false);
  const [createdLicenses, setCreatedLicenses]  = useState([]);

  const baseURL = sessionStorage.getItem('baseURL');

  /* ── Auth guard ──────────────────────────────────────────────── */
  useEffect(() => {
    if (sessionStorage.getItem('isLoggedIn') !== 'true') {
      navigate('/');
      return;
    }
    if (sessionStorage.getItem('isSuperAdmin') === 'true') {
      navigate('/dashboard');
      return;
    }
    fetchDetails();
    document.getElementById('loading').hidden = true;
  }, [navigate]);

  /* ── Fetch image helper ──────────────────────────────────────── */
  const fetchImageAsDataURL = async (imageUrl) => {
    const response = await axios.get(imageUrl, { responseType: 'arraybuffer' });
    const blob = new Blob([response.data], { type: response.headers['content-type'] });
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror  = reject;
      reader.readAsDataURL(blob);
    });
  };

  /* ── Fetch page data ─────────────────────────────────────────── */
  const fetchDetails = async () => {
    try {
      document.getElementById('loading').hidden = false;
      const response = await axios.get(baseURL + 'pu/getGroupIds');
      setGroupIds(response.data.map((u) => u.production_unit_name));

      setTimeout(() => {
        const raw = sessionStorage.getItem('skuData');
        if (!raw) return;
        const d = JSON.parse(raw);
        setProductImage(d.productImage);
        setGtin(d.gtin);
        setGenericName(d.generic_name);
        setManufacturingLic(d.manufacturing_license);
        setGroupId(d.prod_unit);
        setSKUID(d.sku_id);
        setVisitUrl(d.visiturl);
        setExcipients(d.excipients);
        setLicense(d.license_group);
        setProductName(d.brand_name);
        fetchImageAsDataURL(
          baseURL + 'sku/SKUImage/' + d.product_image.replaceAll('/', '-')
        )
          .then(setSkuImage)
          .catch((e) => console.error('SKU image fetch error:', e));
      }, 1000);
    } catch (e) {
      console.error('Error fetching group IDs:', e);
      setGroupIds([]);
    } finally {
      document.getElementById('loading').hidden = true;
    }
  };

  /* ── Toast helper ────────────────────────────────────────────── */
  function showToast(message, type = 'success') {
    const toast = document.createElement('div');
    toast.innerText = message;
    Object.assign(toast.style, {
      position: 'fixed', top: '20px', right: '20px',
      background: type === 'error' ? '#d32f2f' : type === 'success' ? '#2e7d32' : '#ffa000',
      color: '#fff', padding: '12px 18px', borderRadius: '6px',
      boxShadow: '0 4px 12px rgba(0,0,0,0.2)', zIndex: 9999,
      opacity: '0', transition: 'opacity 0.3s ease',
      fontFamily: "'Source Sans 3', sans-serif", fontSize: '.9rem',
    });
    document.body.appendChild(toast);
    requestAnimationFrame(() => (toast.style.opacity = '1'));
    setTimeout(() => {
      toast.style.opacity = '0';
      setTimeout(() => toast.remove(), 300);
    }, 3500);
  }

  /* ── License check ───────────────────────────────────────────── */
  const checkLicense = () => {
    const lic = license.trim();
    const isCreated = Array.isArray(createdLicenses) &&
      createdLicenses.some((item) => item.lic_no === lic);
    if (isCreated) {
      setFieldErrors((prev) => ({
        ...prev,
        license: 'SKU for this License No. is already created.',
      }));
    } else {
      setFieldErrors({});
    }
    return true;
  };

  /* ── Validation ──────────────────────────────────────────────── */
  const handleUpdateSKU = () => {
    const licErr = fieldErrors.license;
    const errors = {};
    if (licErr) errors.license = licErr;

    if (changeImage && !newProductImage) {
      errors.productImage = 'Product Image is required.';
    } else if (changeImage) {
      const allowed = ['image/png', 'image/jpeg'];
      if (!allowed.includes(newProductImage.type))
        errors.productImage = 'Only PNG and JPEG images are allowed.';
      if (newProductImage.size > 250 * 1024)
        errors.productImage = 'File size must be less than 250 KB.';
    }

    if (!gtin.trim())            errors.gtin            = 'GTIN is required.';
    else if (gtin.length !== 14) errors.gtin            = 'GTIN must be 14 digits.';
    if (!genericName.trim())     errors.genericName     = 'Generic Name is required.';
    if (!manufacturingLic.trim())errors.manufacturingLic= 'Manufacturing License is required.';
    if (!groupId.trim())         errors.groupId         = 'Production Unit is required.';
    if (!excipients.trim())      errors.excipients      = 'Excipients are required.';
    if (!visitUrl.trim())        errors.visitUrl        = 'Visit Us URL is required.';

    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) return;

    document.getElementById('loading').hidden = false;
    submitDetails();
  };

  /* ── Submit ──────────────────────────────────────────────────── */
  const submitDetails = () => {
    const fd = new FormData();
    fd.append('sku_id', skuId);
    fd.append('license_key', license);
    fd.append('product_name', productName);
    fd.append('productImage', changeImage ? newProductImage : null);
    fd.append('gtin', gtin);
    fd.append('genericName', genericName);
    fd.append('license', manufacturingLic);
    fd.append('group_id', groupId);
    fd.append('changeImage', changeImage);
    fd.append('excipients', excipients);
    fd.append('visitUrl', visitUrl);

    axios
      .post(baseURL + 'sku/SKUDetailsUpdate', fd, {
        headers: { Authorization: `Bearer ${sessionStorage.getItem('token')}` },
      })
      .then(() => {
        showToast('SKU Data Updated Successfully');
        document.getElementById('loading').hidden = true;
        navigate('/dashboard');
      })
      .catch(() => {
        showToast('Error updating SKU. Please try again.', 'error');
        document.getElementById('loading').hidden = true;
      });
  };

  const handleGtinChange = (e) => {
    if (/^\d*$/.test(e.target.value)) {
      setGtin(e.target.value);
      setFieldErrors((prev) => ({ ...prev, gtin: '' }));
    }
  };

  /* ════════════════════════════════════════════════════════════════
     RENDER
  ════════════════════════════════════════════════════════════════ */
  return (
    <div className="sku-update-page">

      {/* ── Sub-header ── */}
      <div className="sub-header">
        <button className="back-btn" onClick={() => navigate('/dashboard')}>
          ← Back to Dashboard
        </button>
      </div>

      {/* ── Page title ── */}
      <div className="page-title-band">
        <h1>Update Product Details</h1>
        {skuId && <span>SKU ID: {skuId}</span>}
      </div>

      {/* ── Two-panel layout ── */}
      <div className="sku-layout">

        {/* ══ LEFT: Product Card ══ */}
        <div className="product-card">

          <div className="card-band">
            <p className="card-band-title">Product Identity</p>
          </div>

          {/* Product image */}
          <div className="product-image-wrap">
            {skuImage ? (
              <img src={skuImage} alt="Product" />
            ) : (
              <div className="no-image">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <rect x="3" y="3" width="18" height="18" rx="2" />
                  <circle cx="8.5" cy="8.5" r="1.5" />
                  <path d="M21 15l-5-5L5 21" />
                </svg>
                <span>No image available</span>
              </div>
            )}
          </div>

          {/* Read-only identity info */}
          <div className="identity-block">
            <div className="identity-row">
              <span className="identity-label">Product Name</span>
              <span className="identity-value">{productName || '—'}</span>
            </div>
            <div className="identity-row">
              <span className="identity-label">License Key</span>
              <span className="identity-value">{license || '—'}</span>
            </div>
            <div className="identity-row">
              <span className="identity-label">SKU ID</span>
              <span className="identity-value">{skuId || '—'}</span>
            </div>
          </div>

          {/* Image change toggle */}
          <div className="image-toggle-wrap">
            <label className="image-toggle-label">
              <input
                type="checkbox"
                checked={changeImage}
                onChange={(e) => setChangeImage(e.target.checked)}
              />
              Change Product Image
            </label>

            {changeImage && (
              <div className="file-upload-wrap">
                <input
                  type="file"
                  accept="image/*"
                  className={fieldErrors.productImage ? 'is-invalid' : ''}
                  onChange={(e) => setNewProductImage(e.target.files[0])}
                />
                {fieldErrors.productImage && (
                  <div className="invalid-feedback">{fieldErrors.productImage}</div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* ══ RIGHT: Editable Form Card ══ */}
        <div className="form-card">

          <div className="card-band">
            <p className="card-band-title">Editable Product Information</p>
          </div>

          <div className="form-grid">

            {/* GTIN */}
            <div className="form-group">
              <label>GTIN (14 digits) <span className="req">*</span></label>
              <input
                type="text"
                inputMode="numeric"
                pattern="\d{14}"
                maxLength={14}
                className={fieldErrors.gtin ? 'is-invalid' : ''}
                value={gtin}
                onChange={handleGtinChange}
                placeholder="Enter 14-digit GTIN"
              />
              {fieldErrors.gtin && (
                <div className="invalid-feedback">{fieldErrors.gtin}</div>
              )}
            </div>

            {/* Generic Name */}
            <div className="form-group">
              <label>Generic Name of Drug <span className="req">*</span></label>
              <input
                type="text"
                className={fieldErrors.genericName ? 'is-invalid' : ''}
                value={genericName}
                onChange={(e) => {
                  setGenericName(e.target.value);
                  setFieldErrors((p) => ({ ...p, genericName: '' }));
                }}
                placeholder="Enter generic drug name"
              />
              {fieldErrors.genericName && (
                <div className="invalid-feedback">{fieldErrors.genericName}</div>
              )}
            </div>

            {/* Manufacturing Lic */}
            <div className="form-group">
              <label>Manufacturing Lic. No. <span className="req">*</span></label>
              <input
                type="text"
                className={fieldErrors.manufacturingLic ? 'is-invalid' : ''}
                value={manufacturingLic}
                onChange={(e) => {
                  setManufacturingLic(e.target.value);
                  setFieldErrors((p) => ({ ...p, manufacturingLic: '' }));
                }}
                placeholder="Enter manufacturing license number"
              />
              {fieldErrors.manufacturingLic && (
                <div className="invalid-feedback">{fieldErrors.manufacturingLic}</div>
              )}
            </div>

            {/* Production Unit */}
            <div className="form-group">
              <label>Production Unit <span className="req">*</span></label>
              <div className="select-wrap">
                <select
                  className={fieldErrors.groupId ? 'is-invalid' : ''}
                  value={groupId}
                  onChange={(e) => {
                    setGroupId(e.target.value);
                    setFieldErrors((p) => ({ ...p, groupId: '' }));
                  }}
                >
                  <option value="">— Select Production Unit —</option>
                  {groupIds.map((id) => (
                    <option key={id} value={id}>{id}</option>
                  ))}
                </select>
              </div>
              {fieldErrors.groupId && (
                <div className="invalid-feedback">{fieldErrors.groupId}</div>
              )}
            </div>

            <div className="form-group full">
              <label>List of Excipients <span className="req">*</span></label>
              <textarea
                className={fieldErrors.excipients ? 'is-invalid' : ''}
                value={excipients}
                onChange={(e) => {
                  setExcipients(e.target.value);
                  setFieldErrors((p) => ({ ...p, excipients: '' }));
                }}
                placeholder="Excipients List"
              />
              {fieldErrors.excipients && (
                <div className="invalid-feedback">{fieldErrors.excipients}</div>
              )}
            </div>

            {/* Visit URL — full width */}
            <div className="form-group full">
              <label>Visit Us URL <span className="req">*</span></label>
              <input
                type="text"
                className={fieldErrors.visitUrl ? 'is-invalid' : ''}
                value={visitUrl}
                onChange={(e) => {
                  setVisitUrl(e.target.value);
                  setFieldErrors((p) => ({ ...p, visitUrl: '' }));
                }}
                placeholder="https://example.com"
              />
              {fieldErrors.visitUrl && (
                <div className="invalid-feedback">{fieldErrors.visitUrl}</div>
              )}
            </div>

          </div>{/* /form-grid */}

          {/* Action buttons */}
          <div className="form-actions">
            <button className="btn-secondary" onClick={() => navigate('/dashboard')}>
              Cancel
            </button>
            <button className="btn-primary" onClick={handleUpdateSKU}>
              Update Product Details
            </button>
          </div>

        </div>{/* /form-card */}
      </div>{/* /sku-layout */}

      {/* Loading overlay */}
      <div className="loading" id="loading">
        <GridLoader color="#2C3493" loading={true} size={20} />
      </div>

    </div>
  );
}

export default UpdateSKUDetails;